import create from 'zustand';
// Calling actions outside a React event handler needs to be wrapped with `unstable_batchedUpdates`
// (https://github.com/pmndrs/zustand#calling-actions-outside-a-react-event-handler)
import { unstable_batchedUpdates as unstableBatchedUpdates } from 'react-dom';

import type { SectionInfo } from '../types';

type PreviousInfo = {
  previousTime: number;
  previousSectionIndex: number;
  sectionInfoList: SectionInfo[];
};

export const getCurrentSectionIndex = (currentTime: number, previousInfo: PreviousInfo): number => {
  const { previousTime, previousSectionIndex, sectionInfoList } = previousInfo;
  if (sectionInfoList.length === 0 || previousTime === currentTime) {
    // We don't have a list of section, we should not calculate anything.
    return previousSectionIndex;
  }

  const isMovingForward = previousTime < currentTime;
  if (isMovingForward) {
    // We avance in time
    // We are looking for the section in [currentSectionIndex, lastSectionIndex]
    // eslint-disable-next-line no-plusplus
    for (let i = previousSectionIndex; i < sectionInfoList.length; i++) {
      if (i === sectionInfoList.length - 1) {
        // We are at the last section already
        return i;
      }
      const nextSectionStartTime = sectionInfoList[i + 1].startTimeInMs;
      if (currentTime <= nextSectionStartTime) {
        // We are on this section
        return i;
      }
    }
  } else {
    // We go back in time
    // We are looking for the section in [0, currentSectionIndex]
    // We loop backward
    // eslint-disable-next-line no-plusplus
    for (let i = previousSectionIndex; i >= 0; i--) {
      if (i === 0) {
        // We are at the first section already
        return i;
      }
      const sectionStartTime = sectionInfoList[i].startTimeInMs;
      if (currentTime > sectionStartTime) {
        // We are on this section
        return i;
      }
    }
  }
  return previousSectionIndex;
};

const computePrecision = (durationInMs: number): number => {
  // In order to minimize the number of rerender, we hook components to an approximated time.
  // For bigger recording 1000 ms precision is good.
  // But for smaller one, it creates some problems like the hover is not precise enough or the progress handle is jumpy.

  if (durationInMs > 5 * 60 * 1000) {
    // Recording above 5 min will use a 1000 millisecond precision
    return 1000;
  }
  if (durationInMs > 60 * 1000) {
    // Recording between 1 min and 5 min will use a 100 millisecond precision
    return 100;
  }

  // Recording below 1 min will have a 10 millisecond precision
  return 10;
};

const roundWithCustomPrecision = (
  timeInMs: number,
  customPrecision: number,
  maxValue: number,
): number => {
  return Math.min(Math.round(timeInMs / customPrecision) * customPrecision, maxValue);
};

export type State = {
  currentTime: number;
  // currentTimeWithLessPrecision is currentTime rounded by second: 2222ms => 2000ms
  // It is used in the timeline and the action tab to reduce the amount of re-render.
  currentTimeWithLessPrecision: number;
  currentSectionIndex: number;
  currentPageIndex: number;
  currentTabIndex: number;
  draggedTime: number;
  draggedSectionIndex: number;
  draggedPageIndex: number;
  hoveredTime: number;
  hoveredSectionIndex: number;
  hoveredPageIndex: number;
  isRelevantActionOverlappingHighlight: boolean;
  isDragging: boolean;
  isHovering: boolean;
  sectionInfoList: SectionInfo[];
  duration: number;
  precisionInMs: number;
  tabCount: number;
  setCurrentTime: (newCurrentTime: number) => void;
  setDraggedTime: (newDraggedTime: number) => void;
  setHoveredTime: (newHoveredTime: number) => void;
  setAllTimeAfterDrag: (newAllTimes: number) => void;
  setIsDragging: (newIsDragging: boolean) => void;
  setIsHovering: (newIsHovering: boolean) => void;
  setSectionInfoList: (newSectionInfoList: SectionInfo[]) => void;
  setDuration: (newHoveredTime: number) => void;
  setTabCount: (newTabCount: number) => void;
  setIsRelevantActionOverlappingHighlight: (isOverlapping: boolean) => void;
  setDefaultValues: () => void;
};

const defaultState = {
  currentTime: 0,
  currentTimeWithLessPrecision: 0,
  currentSectionIndex: 0,
  currentPageIndex: 0,
  currentTabIndex: 0,
  draggedTime: 0,
  draggedSectionIndex: 0,
  draggedPageIndex: 0,
  hoveredTime: 0,
  hoveredSectionIndex: 0,
  hoveredPageIndex: 0,
  isDragging: false,
  isRelevantActionOverlappingHighlight: false,
  isHovering: false,
  sectionInfoList: [],
  duration: 0,
  precisionInMs: 1000,
  tabCount: 0,
};

export const useRealTimeStore = create<State>((set) => ({
  ...defaultState,
  setCurrentTime: (newCurrentTime) =>
    set((state) => {
      const timeInMsWithLessPrecision = roundWithCustomPrecision(
        newCurrentTime,
        state.precisionInMs,
        state.duration,
      );
      // Note that we need to calculate currentSectionIndex with the precise time newCurrentTime:
      // - to find the correct section to jump to with GO_TO_PREVIOUS_CHAPTER or GO_TO_NEXT_CHAPTER shortcut
      // - to show the "skip inactivity" button exactly when it could be triggered
      const newSectionIndex = getCurrentSectionIndex(newCurrentTime, {
        previousTime: state.currentTime,
        previousSectionIndex: state.currentSectionIndex,
        sectionInfoList: state.sectionInfoList,
      });
      return {
        currentTime: newCurrentTime,
        currentTimeWithLessPrecision: timeInMsWithLessPrecision,
        currentSectionIndex: newSectionIndex,
        currentPageIndex: state.sectionInfoList[newSectionIndex]?.pageIndex,
        currentTabIndex: state.sectionInfoList[newSectionIndex]?.tabIndex,
      };
    }),
  setDraggedTime: (newDraggedTime) =>
    set((state) => {
      const timeInMsWithLessPrecision = roundWithCustomPrecision(
        newDraggedTime,
        state.precisionInMs,
        state.duration,
      );
      const newSectionIndex = getCurrentSectionIndex(timeInMsWithLessPrecision, {
        previousTime: state.draggedTime,
        previousSectionIndex: state.draggedSectionIndex,
        sectionInfoList: state.sectionInfoList,
      });
      return {
        draggedTime: timeInMsWithLessPrecision,
        draggedSectionIndex: newSectionIndex,
        draggedPageIndex: state.sectionInfoList[newSectionIndex]?.pageIndex,
      };
    }),
  setHoveredTime: (newHoveredTime) =>
    set((state) => {
      const timeInMsWithLessPrecision = roundWithCustomPrecision(
        newHoveredTime,
        state.precisionInMs,
        state.duration,
      );
      const newSectionIndex = getCurrentSectionIndex(timeInMsWithLessPrecision, {
        previousTime: state.hoveredTime,
        previousSectionIndex: state.hoveredSectionIndex,
        sectionInfoList: state.sectionInfoList,
      });
      return {
        hoveredTime: timeInMsWithLessPrecision,
        hoveredSectionIndex: newSectionIndex,
        hoveredPageIndex: state.sectionInfoList[newSectionIndex]?.pageIndex,
      };
    }),
  setAllTimeAfterDrag: (newDraggedTime) =>
    set((state) => {
      const timeInMsWithLessPrecision = roundWithCustomPrecision(
        newDraggedTime,
        state.precisionInMs,
        state.duration,
      );
      const newSectionIndex = getCurrentSectionIndex(timeInMsWithLessPrecision, {
        previousTime: state.draggedTime,
        previousSectionIndex: state.draggedSectionIndex,
        sectionInfoList: state.sectionInfoList,
      });
      const newPageIndex = state.sectionInfoList[newSectionIndex]?.pageIndex;
      const newTabIndex = state.sectionInfoList[newSectionIndex]?.tabIndex;
      return {
        currentTime: timeInMsWithLessPrecision,
        currentTimeWithLessPrecision: timeInMsWithLessPrecision,
        hoveredTime: timeInMsWithLessPrecision,
        draggedTime: timeInMsWithLessPrecision,
        currentSectionIndex: newSectionIndex,
        hoveredSectionIndex: newSectionIndex,
        draggedSectionIndex: newSectionIndex,
        currentPageIndex: newPageIndex,
        currentTabIndex: newTabIndex,
        hoveredPageIndex: newPageIndex,
        draggedPageIndex: newPageIndex,
      };
    }),
  setIsDragging: (newIsDragging: boolean) => set(() => ({ isDragging: newIsDragging })),
  setIsHovering: (newIsHovering: boolean) => set(() => ({ isHovering: newIsHovering })),
  setSectionInfoList: (newSectionInfoList: SectionInfo[]) =>
    set(() => ({ sectionInfoList: newSectionInfoList })),
  setDuration: (newDuration: number) =>
    set(() => {
      return { duration: newDuration, precisionInMs: computePrecision(newDuration) };
    }),
  setIsRelevantActionOverlappingHighlight: (isOverlapping: boolean) =>
    set(() => ({ isRelevantActionOverlappingHighlight: isOverlapping })),
  setTabCount: (newTabCount: number) => set(() => ({ tabCount: newTabCount })),
  setDefaultValues: () => set(() => ({ ...defaultState })),
}));

// To update state from angular, call this:
export const nonReactSetCurrentTime = (newTime: number): void => {
  unstableBatchedUpdates(() => {
    useRealTimeStore.getState().setCurrentTime(newTime);
  });
};

export const nonReactSetSectionInfoList = (newSectionInfoList: SectionInfo[]): void => {
  unstableBatchedUpdates(() => {
    useRealTimeStore.getState().setSectionInfoList(newSectionInfoList);
  });
};

export const nonReactSetDuration = (newDuration: number): void => {
  unstableBatchedUpdates(() => {
    useRealTimeStore.getState().setDuration(newDuration);
  });
};

export const nonReactSetTabCount = (newTabCount: number): void => {
  unstableBatchedUpdates(() => {
    useRealTimeStore.getState().setTabCount(newTabCount);
  });
};

export const nonReactSetDefaultRealTimeStoreValues = (): void => {
  unstableBatchedUpdates(() => {
    useRealTimeStore.getState().setDefaultValues();
  });
};

// To see all the changes happening, uncomment the next line:
// const unsub = useRealTimeStore.subscribe(console.log);
